/* sn-money.js — localise hardcoded UK-centric copy for the visitor's Shopify
 * market: GBP amounts into their presentment currency, and "Royal Mail" into
 * their own postal carrier.
 *
 * (The filename predates the carrier half. It stays put because it's wired into
 * six sections; renaming would churn all of them for no behavioural gain.)
 *
 * Liquid money objects (product prices etc.) already localise server-side, but
 * the SEO landing pages and some theme-editor copy hard-code amounts like
 * "£2.99/mo" or "£120 consult" as plain text. On a non-GBP storefront those
 * stay GBP — this script converts them client-side:
 *
 *   - Shopify injects `Shopify.currency = {active, rate}` on every page, where
 *     `rate` converts FROM the shop currency (GBP) TO the active currency.
 *   - We walk text nodes inside `.sn-page` / `[data-sn-money]` scopes, match
 *     £-amounts, multiply by the rate and format with Intl.NumberFormat in the
 *     visitor's locale. Output is prefixed "~" (it's an approximation at the
 *     current market rate, not a billed amount).
 *   - GBP visitors: no-op. <script>/<style> (incl. JSON-LD) are never touched,
 *     so structured data and crawled content stay canonical GBP.
 *   - Anything inside `[data-sn-money-skip]` is left alone, subtree and all.
 *     Use it for amounts that are facts rather than prices — UK statutory fines
 *     (Control of Dogs Order: £500 / £2,500), government fees, legal thresholds.
 *     Converting those to "~$640" would be actively wrong.
 *   - Idempotent: converted text contains no "£", so re-walking (or the
 *     MutationObserver below catching widget re-renders, e.g. the
 *     crumb-vs-supernormal verdict swap) can't double-convert.
 *   - Zero is NOT prefixed with "~". Zero converts exactly in every currency,
 *     so "~0 €" claimed an approximation where none exists. "£0" → "0 €".
 *
 * CARRIERS — `<span data-sn-carrier>Royal Mail</span>` has its text replaced
 * with the visitor's own postal service ("USPS", "Deutsche Post", …), keyed on
 * `Shopify.country`. Use it only where the sentence is about the reader's OWN
 * delivery. Statements of fact about a specific country ("Ireland is the same
 * £2.99, sent by An Post") or about a competitor ("£15.00 plus £2.99 Royal
 * Mail") must be left as plain text — swapping those would make them false.
 *
 * ⚠️ The country→carrier map below MIRRORS the Liquid one in
 * `snippets/product-inventory.liquid`, which serves the product pages. Liquid
 * can't run inside a page/article body, hence the duplicate. Change both.
 *
 * No dependencies. Safe to load with `defer` on any page; does nothing when
 * the scopes are absent.
 */
(function () {
  'use strict';

  // Mirrors snippets/product-inventory.liquid — keep the two in step.
  var CARRIERS = {
    GB: 'Royal Mail', US: 'USPS', CA: 'Canada Post', NZ: 'NZ Post',
    AU: 'Australia Post', FR: 'La Poste', DE: 'Deutsche Post', NL: 'PostNL',
    BE: 'bpost', ES: 'Correos', IT: 'Poste Italiane', IE: 'An Post',
    CH: 'Swiss Post', SE: 'PostNord', DK: 'PostNord', NO: 'Posten Norge',
    FI: 'Posti',
  };
  var CARRIER_FALLBACK = 'your local postal service';

  // Runs regardless of currency — an Irish visitor is on EUR but a GB-market
  // visitor is on GBP, and both need the carrier corrected from the default.
  function localiseCarriers() {
    var country = window.Shopify && window.Shopify.country;
    if (!country) return; // unknown market — leave the authored default
    var name = CARRIERS[country] || CARRIER_FALLBACK;
    var els = document.querySelectorAll('[data-sn-carrier]');
    for (var i = 0; i < els.length; i++) {
      if (els[i].textContent !== name) els[i].textContent = name;
    }
  }

  function boot() {
    localiseCarriers();

    var cur = window.Shopify && window.Shopify.currency;
    if (!cur || !cur.active || cur.active === 'GBP') return;
    var rate = parseFloat(cur.rate);
    if (!isFinite(rate) || rate <= 0) return;

    var fmtExact, fmtWhole;
    try {
      // fmtExact: currency-default decimals (USD → 2, JPY → 0).
      // fmtWhole: for amounts written as whole pounds (£120) keep them whole.
      fmtExact = new Intl.NumberFormat(undefined, { style: 'currency', currency: cur.active });
      fmtWhole = new Intl.NumberFormat(undefined, { style: 'currency', currency: cur.active, maximumFractionDigits: 0 });
    } catch (e) { return; } // unknown currency code — leave GBP rather than break

    // Two written forms, because the EN and DE copy differ:
    //   prefix  "£1,234.56"  — EN convention: "," thousands, "." decimal
    //   postfix "1.234,56 £" — DE convention: "." thousands, "," decimal
    // The DE storefront is full of the postfix form ("2,99 £", "120 £"), which a
    // prefix-only pattern silently skipped, leaving German visitors on GBP.
    // Optional leading "~" is absorbed so "~£4.95" doesn't become "~~…".
    var RE = /(~\s?)?(?:£\s?(\d[\d,]*(?:\.\d+)?)|(\d[\d.]*(?:,\d+)?)\s?£)/g;

    function convertText(s) {
      return s.replace(RE, function (m, _tilde, pre, post) {
        var gbp, whole;
        if (pre !== undefined) {
          gbp = parseFloat(pre.replace(/,/g, ''));
          whole = pre.indexOf('.') === -1;
        } else {
          gbp = parseFloat(post.replace(/\./g, '').replace(',', '.'));
          whole = post.indexOf(',') === -1;
        }
        if (!isFinite(gbp)) return m;
        // Zero is exact in every currency — "~0 €" would claim an approximation
        // that isn't one. Everything else gets the "~".
        var tilde = gbp === 0 ? '' : '~';
        // Intl places the symbol per the visitor's locale, so de-DE gets "9,99 €".
        return tilde + (whole ? fmtWhole : fmtExact).format(gbp * rate);
      });
    }

    var SKIP = '[data-sn-money-skip]';

    function skipped(n) {
      // closest() walks up through ancestors; guard for text nodes and detached bits.
      var el = n.nodeType === 3 ? n.parentNode : n;
      return !!(el && el.closest && el.closest(SKIP));
    }

    function walk(root) {
      if (root.nodeType === 3) { // bare text node (from MutationObserver)
        if (root.nodeValue.indexOf('£') !== -1 && !skipped(root)) root.nodeValue = convertText(root.nodeValue);
        return;
      }
      if (root.nodeType !== 1) return;
      if (root.matches && root.matches(SKIP)) return; // the whole subtree opts out
      var w = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
        acceptNode: function (n) {
          var p = n.parentNode && n.parentNode.nodeName;
          if (p === 'SCRIPT' || p === 'STYLE' || p === 'NOSCRIPT' || p === 'TEMPLATE') return NodeFilter.FILTER_REJECT;
          if (n.nodeValue.indexOf('£') === -1) return NodeFilter.FILTER_SKIP;
          return skipped(n) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT;
        },
      });
      var nodes = [];
      while (w.nextNode()) nodes.push(w.currentNode); // collect first; don't mutate mid-walk
      for (var i = 0; i < nodes.length; i++) nodes[i].nodeValue = convertText(nodes[i].nodeValue);
    }

    var scopes = document.querySelectorAll('.sn-page, [data-sn-money]');
    Array.prototype.forEach.call(scopes, function (scope) {
      walk(scope);
      // Widgets re-render text client-side (e.g. the CompareTable verdict swap);
      // convert anything new. Our own writes contain no "£", so no loop.
      new MutationObserver(function (muts) {
        for (var i = 0; i < muts.length; i++) {
          var m = muts[i];
          if (m.type === 'characterData') walk(m.target);
          else for (var j = 0; j < m.addedNodes.length; j++) walk(m.addedNodes[j]);
        }
      }).observe(scope, { subtree: true, childList: true, characterData: true });
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
